import React, { useMemo } from 'react'
import { Card, Table, Typography } from 'antd'

const { Text } = Typography

/**
 * Componente Data Grid para exibir agregação por CD -> Rota -> Cliente
 * Mostra colunas: CD, Rota, Cliente, Volumes, Peso, M3
 * Aceita dados brutos (array) contendo campos: CD, CLIENTE, Rota/rota, VOLUMES, PESO, M3
 */
const CDPracaGrid = ({ data = [], loading = false }) => {
  // Preparar dados agrupados por CD, Rota e Cliente
  const groupedData = useMemo(() => {
    const byCD = {}

    const toNumber = (val) => {
      if (val === null || val === undefined) return 0
      const n = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'))
      return Number.isFinite(n) ? n : 0
    }

    (Array.isArray(data) ? data : []).forEach((item) => {
      const cd = String(item?.CD ?? '').trim() || '—'
      const cliente = String(item?.CLIENTE ?? '').trim() || '—'
      const rota = String(item?.Rota ?? item?.rota ?? '').trim() || '—'

      const volumes = toNumber(item?.VOLUMES)
      const peso = toNumber(item?.PESO)
      const m3 = toNumber(item?.M3)

      if (!byCD[cd]) {
        byCD[cd] = {
          cd,
          volumes: 0,
          peso: 0,
          m3: 0,
          rotasMap: {},
        }
      }

      byCD[cd].volumes += volumes
      byCD[cd].peso += peso
      byCD[cd].m3 += m3

      if (!byCD[cd].rotasMap[rota]) {
        byCD[cd].rotasMap[rota] = {
          rota,
          volumes: 0,
          peso: 0,
          m3: 0,
          clientsMap: {},
        }
      }

      byCD[cd].rotasMap[rota].volumes += volumes
      byCD[cd].rotasMap[rota].peso += peso
      byCD[cd].rotasMap[rota].m3 += m3

      if (!byCD[cd].rotasMap[rota].clientsMap[cliente]) {
        byCD[cd].rotasMap[rota].clientsMap[cliente] = {
          cliente,
          volumes: 0,
          peso: 0,
          m3: 0,
        }
      }

      byCD[cd].rotasMap[rota].clientsMap[cliente].volumes += volumes
      byCD[cd].rotasMap[rota].clientsMap[cliente].peso += peso
      byCD[cd].rotasMap[rota].clientsMap[cliente].m3 += m3
    })

    // Converter para dataSource com children
    const rows = Object.values(byCD)
      .sort((a, b) => String(a.cd).localeCompare(String(b.cd)))
      .map((cdNode) => ({
        key: `cd_${cdNode.cd}`,
        cd: cdNode.cd,
        rota: '',
        cliente: '',
        volumes: cdNode.volumes,
        peso: cdNode.peso,
        m3: cdNode.m3,
        children: Object.values(cdNode.rotasMap)
          .sort((a, b) => String(a.rota).localeCompare(String(b.rota)))
          .map((roNode) => ({
            key: `cd_${cdNode.cd}_ro_${roNode.rota}`,
            cd: cdNode.cd,
            rota: roNode.rota,
            cliente: '',
            volumes: roNode.volumes,
            peso: roNode.peso,
            m3: roNode.m3,
            children: Object.values(roNode.clientsMap)
              .sort((a, b) => String(a.cliente).localeCompare(String(b.cliente)))
              .map((cliNode) => ({
                key: `cd_${cdNode.cd}_ro_${roNode.rota}_cli_${cliNode.cliente}`,
                cd: cdNode.cd,
                rota: roNode.rota,
                cliente: cliNode.cliente,
                volumes: cliNode.volumes,
                peso: cliNode.peso,
                m3: cliNode.m3,
              })),
          })),
      }))

    return rows
  }, [data])

  const columns = [
    {
      title: 'CD',
      dataIndex: 'cd',
      key: 'cd',
      render: (text) => <Text strong>{text}</Text>,
      sorter: (a, b) => String(a.cd || '').localeCompare(String(b.cd || '')),
      width: 120,
    },
    {
      title: 'Rota',
      dataIndex: 'rota',
      key: 'rota',
      render: (text) => text || <span style={{ color: '#999' }}>—</span>,
      sorter: (a, b) => String(a.rota || '').localeCompare(String(b.rota || '')),
      width: 140,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente',
      key: 'cliente',
      render: (text) => text || <span style={{ color: '#999' }}>—</span>,
      sorter: (a, b) => String(a.cliente || '').localeCompare(String(b.cliente || '')),
      width: 180,
    },
    {
      title: 'Volumes',
      dataIndex: 'volumes',
      key: 'volumes',
      align: 'right',
      sorter: (a, b) => (a.volumes || 0) - (b.volumes || 0),
      width: 110,
    },
    {
      title: 'Peso',
      dataIndex: 'peso',
      key: 'peso',
      align: 'right',
      render: (val) => (val ?? 0).toFixed(2),
      sorter: (a, b) => (a.peso || 0) - (b.peso || 0),
      width: 110,
    },
    {
      title: 'M3',
      dataIndex: 'm3',
      key: 'm3',
      align: 'right',
      render: (val) => (val ?? 0).toFixed(2),
      sorter: (a, b) => (a.m3 || 0) - (b.m3 || 0),
      width: 110,
    },
  ]

  return (
    <Card
      title="CD/Rota/Cliente - Cargas do Dia"
      size="small"
      styles={{ body: { padding: 0 } }}
      style={{ marginTop: 12 }}
    >
      <div style={{ width: '100%', overflowX: 'auto', maxWidth: '100%' }}>
        <Table
          dataSource={groupedData}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          size="small"
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          style={{ fontSize: '12px' }}
          expandable={{
            expandedRowClassName: () => 'table-row-dark',
          }}
          rowClassName={(record, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
      </div>
    </Card>
  )
}

export default CDPracaGrid